import React, { createContext, useContext, useEffect, useState } from 'react';

interface FacebookPixelContextType {
  pixelId: string | null;
  setPixelId: (id: string | null) => void;
  isPixelLoaded: boolean;
  trackEvent: (eventName: string, parameters?: object) => void;
}

const FacebookPixelContext = createContext<FacebookPixelContextType | undefined>(undefined);

export const useFacebookPixel = () => {
  const context = useContext(FacebookPixelContext);
  if (context === undefined) {
    throw new Error('useFacebookPixel must be used within a FacebookPixelProvider');
  }
  return context;
};

interface FacebookPixelProviderProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    fbq: any;
  }
}

export const FacebookPixelProvider: React.FC<FacebookPixelProviderProps> = ({ children }) => {
  const [pixelId, setPixelId] = useState<string | null>(null);
  const [isPixelLoaded, setIsPixelLoaded] = useState(false);

  const loadFacebookPixel = (id: string) => {
    if (!id) return;

    // Remove existing pixel script if present
    const existingScript = document.querySelector('script[src*="fbevents.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Remove existing noscript tag
    const existingNoscript = document.querySelector('noscript img[src*="facebook.com/tr"]');
    if (existingNoscript?.parentElement) {
      existingNoscript.parentElement.remove();
    }

    // Reset fbq if it exists
    if (window.fbq) {
      delete window.fbq;
    }

    // Create and inject Facebook Pixel script
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1" />
    `;
    document.body.appendChild(noscript);

    setIsPixelLoaded(true);
  };

  const removeFacebookPixel = () => {
    // Remove script
    const existingScript = document.querySelector('script[src*="fbevents.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Remove noscript
    const existingNoscript = document.querySelector('noscript img[src*="facebook.com/tr"]');
    if (existingNoscript?.parentElement) {
      existingNoscript.parentElement.remove();
    }

    // Reset fbq
    if (window.fbq) {
      delete window.fbq;
    }

    setIsPixelLoaded(false);
  };

  useEffect(() => {
    if (pixelId) {
      loadFacebookPixel(pixelId);
    } else {
      removeFacebookPixel();
    }
  }, [pixelId]);

  const trackEvent = (eventName: string, parameters?: object) => {
    if (window.fbq && pixelId) {
      window.fbq('track', eventName, parameters);
    }
  };

  const contextValue: FacebookPixelContextType = {
    pixelId,
    setPixelId,
    isPixelLoaded,
    trackEvent,
  };

  return (
    <FacebookPixelContext.Provider value={contextValue}>
      {children}
    </FacebookPixelContext.Provider>
  );
};